import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ListChecks, Settings2, GraduationCap, ScrollText, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useUrlState } from "@/lib/useUrlState";
import { ComplianceChecklist } from "@/components/compliance/ComplianceChecklist";
import { ComplianceSettingsCard } from "@/components/compliance/ComplianceSettingsCard";
import { TrainingAcks } from "@/components/compliance/TrainingAcks";
import { AuditLogTable } from "@/components/compliance/AuditLogTable";

const TABS = [
  { key: "checklist", label: "Checklist", icon: ListChecks },
  { key: "settings", label: "Safeguards", icon: Settings2 },
  { key: "training", label: "Training", icon: GraduationCap },
  { key: "audit", label: "Audit Log", icon: ScrollText },
];

export default function CompliancePage() {
  const [tab, setTab] = useUrlState("tab", "checklist");
  const [checklist, setChecklist] = useState(null);

  const load = useCallback(() => {
    api.get("/compliance/checklist").then((r) => setChecklist(r.data)).catch(() => setChecklist({ items: [], score: 0, total: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8" data-testid="compliance-page">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary"><ShieldCheck className="h-4 w-4" /> HIPAA</div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-1">Compliance</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Technical and administrative safeguards for protected health information: encryption, audit trail, automatic logoff, two-step verification, workforce training and record access.</p>
        </div>
        {checklist ? (
          <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-right" data-testid="compliance-score">
            <div className="text-xs uppercase tracking-widest text-slate-500">Safeguards in place</div>
            <div className={`font-display text-3xl font-bold tracking-tighter ${checklist.score === checklist.total ? "text-emerald-600" : "text-slate-900"}`}>{checklist.score}<span className="text-slate-400 text-xl">/{checklist.total}</span></div>
          </div>
        ) : <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
      </header>

      <div className="mb-6 flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="compliance-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`compliance-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition -mb-px ${tab === t.key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "checklist" && <ComplianceChecklist data={checklist} onGo={setTab} />}
      {tab === "settings" && <ComplianceSettingsCard onSaved={load} />}
      {tab === "training" && <TrainingAcks />}
      {tab === "audit" && <AuditLogTable />}
    </div>
  );
}
